import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Box,
    useTheme,
    useMediaQuery,
    Container,
    Avatar,
    Chip,
    Menu,
    MenuItem,
    Divider,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import PhoneIcon from '@mui/icons-material/Phone';
import LoginIcon from '@mui/icons-material/Login';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState(null);
    const isUser = localStorage.getItem('isUser') === 'true';

    const menuItems = [
        { text: 'Home', path: '/', icon: <HomeIcon /> },
        { text: 'Events', path: '/events', icon: <EventIcon /> },
    ];

    // Effet pour détecter le scroll
    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 50;
            setScrolled(isScrolled);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const handleAvatarClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleLogout = () => {
        localStorage.removeItem('isUser');
        setAnchorEl(null);
        window.location.href = '/';
    };

    const drawer = (
        <Box sx={{ width: 280, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, mb: 3 }}>
                <SportsSoccerIcon sx={{ fontSize: 32, color: '#FF6B6B', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B6B' }}>
                    Pérou Sports
                </Typography>
                <IconButton
                    onClick={handleDrawerToggle}
                    sx={{ ml: 'auto', color: 'grey.600' }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <List>
                {menuItems.map((item) => (
                    <ListItem
                        key={item.text}
                        component={Link}
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{
                            mx: 2,
                            mb: 1,
                            borderRadius: 2,
                            backgroundColor: isActive(item.path) ? '#FF6B6B' : 'transparent',
                            color: isActive(item.path) ? 'white' : 'text.primary',
                            '&:hover': {
                                backgroundColor: isActive(item.path) ? '#E53E3E' : 'grey.100',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                            {item.icon}
                        </Box>
                        <ListItemText
                            primary={item.text}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontWeight: isActive(item.path) ? 600 : 400
                                }
                            }}
                        />
                    </ListItem>
                ))}
            </List>

            <Box sx={{ px: 3, mt: 4 }}>
                {isUser ? (
                    <>
                        <IconButton onClick={handleAvatarClick} sx={{ ml: 2, color: scrolled ? '#FF6B6B' : 'white', p: 0 }}>
                            <Avatar sx={{ bgcolor: '#FF6B6B', width: 40, height: 40 }}>
                                <AccountCircleIcon fontSize="large" />
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                sx: {
                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                    color: 'white',
                                    borderRadius: 3,
                                    boxShadow: '0 8px 32px rgba(255,107,107,0.18)',
                                    minWidth: 180,
                                    p: 0,
                                }
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem component={Link} to="/user-list" onClick={handleMenuClose} sx={{ fontWeight: 600, fontSize: 16, justifyContent: 'center', borderRadius: 2, my: 0.5, '&:hover': { background: 'rgba(255,255,255,0.12)' } }}>
                                Your list
                            </MenuItem>
                            <Divider sx={{ my: 0, borderColor: 'rgba(255,255,255,0.18)' }} />
                            <MenuItem onClick={handleLogout} sx={{ fontWeight: 600, fontSize: 16, justifyContent: 'center', borderRadius: 2, my: 0.5, '&:hover': { background: 'rgba(255,255,255,0.18)' } }}>
                                Logout
                            </MenuItem>
                        </Menu>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<LoginIcon />}
                        component={Link}
                        to="/auth"
                        sx={{
                            background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                        }}
                    >
                        Login
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                elevation={scrolled ? 8 : 0}
                sx={{
                    background: scrolled
                        ? 'rgba(255, 255, 255, 0.95)'
                        : 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                    backdropFilter: scrolled ? 'blur(10px)' : 'none',
                    transition: 'all 0.3s ease',
                    color: scrolled ? 'text.primary' : 'white',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar sx={{ px: { xs: 0 }, py: scrolled ? 1 : 2 }}>
                        {/* Menu hamburger pour mobile */}
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{
                                mr: 2,
                                display: { md: 'none' },
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Logo */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    mr: 2,
                                    background: scrolled ? '#FF6B6B' : 'rgba(255,255,255,0.2)',
                                    border: scrolled ? 'none' : '2px solid rgba(255,255,255,0.3)',
                                    color: scrolled ? 'white' : 'white',
                                }}
                            >
                                <SportsSoccerIcon />
                            </Avatar>
                            <Typography
                                variant="h6"
                                component={Link}
                                to="/"
                                sx={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    fontWeight: 'bold',
                                    fontSize: scrolled ? '1.1rem' : '1.3rem',
                                    transition: 'font-size 0.3s ease',
                                    '&:hover': {
                                        opacity: 0.8,
                                    },
                                }}
                            >
                                Pérou Sports
                            </Typography>
                        </Box>

                        {/* Navigation desktop */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {menuItems.map((item) => (
                                    <Button
                                        key={item.text}
                                        component={Link}
                                        to={item.path}
                                        startIcon={item.icon}
                                        sx={{
                                            color: 'inherit',
                                            backgroundColor: isActive(item.path)
                                                ? 'rgba(255,255,255,0.2)'
                                                : 'transparent',
                                            borderRadius: 2,
                                            px: 3,
                                            py: 1,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '0.95rem',
                                            '&:hover': {
                                                backgroundColor: isActive(item.path)
                                                    ? 'rgba(255,255,255,0.3)'
                                                    : 'rgba(255,255,255,0.1)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {item.text}
                                    </Button>
                                ))}
                                {isUser ? (
                                    <>
                                        <IconButton onClick={handleAvatarClick} sx={{ ml: 2, color: scrolled ? '#FF6B6B' : 'white', p: 0 }}>
                                            <Avatar sx={{ bgcolor: '#FF6B6B', width: 40, height: 40 }}>
                                                <AccountCircleIcon fontSize="large" />
                                            </Avatar>
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleMenuClose}
                                            PaperProps={{
                                                sx: {
                                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                                    color: 'white',
                                                    borderRadius: 3,
                                                    boxShadow: '0 8px 32px rgba(255,107,107,0.18)',
                                                    minWidth: 180,
                                                    p: 0,
                                                }
                                            }}
                                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                        >
                                            <MenuItem component={Link} to="/user-list" onClick={handleMenuClose} sx={{ fontWeight: 600, fontSize: 16, justifyContent: 'center', borderRadius: 2, my: 0.5, '&:hover': { background: 'rgba(255,255,255,0.12)' } }}>
                                                Your list
                                            </MenuItem>
                                            <Divider sx={{ my: 0, borderColor: 'rgba(255,255,255,0.18)' }} />
                                            <MenuItem onClick={handleLogout} sx={{ fontWeight: 600, fontSize: 16, justifyContent: 'center', borderRadius: 2, my: 0.5, '&:hover': { background: 'rgba(255,255,255,0.18)' } }}>
                                                Logout
                                            </MenuItem>
                                        </Menu>
                                    </>
                                ) : (
                                    <Button
                                        variant="contained"
                                        startIcon={<LoginIcon />}
                                        component={Link}
                                        to="/auth"
                                        sx={{
                                            ml: 2,
                                            backgroundColor: scrolled ? '#FF6B6B' : 'rgba(255,255,255,0.2)',
                                            color: scrolled ? 'white' : 'white',
                                            borderRadius: 2,
                                            px: 3,
                                            py: 1,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.3)',
                                            '&:hover': {
                                                backgroundColor: scrolled ? '#E53E3E' : 'rgba(255,255,255,0.3)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        Login
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Espace pour compenser le header fixe */}
            <Box sx={{ height: scrolled ? 60 : 70, transition: 'height 0.3s ease' }} />

            {/* Drawer mobile */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: 280,
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Header; 